import type { Point, TileWrite, WaterFeature, WorldGeneratorConfig } from './types';
import { WaterAutoTile } from './WaterAutoTile';

export interface WaterTilePlan {
  readonly writes: readonly TileWrite[];
  readonly bridgeCandidates: readonly Point[];
  readonly invalidWater: readonly string[];
  readonly notes: readonly string[];
}

export class WaterTilePlanner {
  constructor(private readonly config: WorldGeneratorConfig) {}

  plan(features: readonly WaterFeature[]): WaterTilePlan {
    const writes = new Map<string, TileWrite>();
    const invalidWater: string[] = [];
    const notes: string[] = [];

    for (const feature of features) {
      if (feature.kind === 'lake') {
        this.writeLake(writes, feature.tiles, feature.shoreline, feature.islands);
        if (!this.isConnected(feature.tiles)) invalidWater.push(`See ${feature.id} ist nicht zusammenhängend.`);
        if (feature.tiles.length < 8) invalidWater.push(`See ${feature.id} ist zu klein für eine glaubwürdige Wasserfläche.`);
        continue;
      }

      this.writeRiver(writes, feature.path, feature.width);
      if (feature.path.length < 6) invalidWater.push(`Fluss ${feature.id} ist zu kurz.`);
      if (samePoint(feature.source, feature.mouth)) invalidWater.push(`Fluss ${feature.id} hat Quelle und Mündung auf demselben Feld.`);
    }

    const autoTiledWrites = new WaterAutoTile().apply([...writes.values()]);
    const waterWrites = autoTiledWrites.filter((write) => write.type === 'water');
    for (const isolated of this.findIsolatedWater(waterWrites)) {
      invalidWater.push(`Isoliertes Wasserfeld bei ${isolated.x}:${isolated.y}.`);
    }

    notes.push(`WaterTilePlanner aktiv: ${waterWrites.length} Wasser-Tiles logisch geplant und autotiled.`);

    return {
      writes: autoTiledWrites,
      bridgeCandidates: this.findBridgeCandidates(waterWrites),
      invalidWater,
      notes,
    };
  }

  private writeLake(writes: Map<string, TileWrite>, tiles: readonly Point[], shoreline: readonly Point[], islands: readonly Point[]): void {
    for (const tile of tiles) {
      const existing = writes.get(key(tile));
      if (existing?.variant?.startsWith('river_')) continue;
      this.write(writes, tile, 'water', 'lake');
    }
    for (const tile of shoreline) {
      if (writes.has(key(tile))) continue;
      this.write(writes, tile, 'grass', 'shore_candidate');
    }
    for (const tile of islands) this.write(writes, tile, 'grass', 'lake_island');
  }

  private writeRiver(writes: Map<string, TileWrite>, path: readonly Point[], width: number): void {
    const radius = Math.max(0, width - 1);
    for (let index = 0; index < path.length; index++) {
      const point = path[index];
      this.write(writes, point, 'water', this.variantForRiver(path[index - 1], point, path[index + 1]));
      if (radius <= 0) continue;
      for (const expanded of this.riverShoulders(path[index - 1], point, path[index + 1], radius)) {
        if (this.isInside(expanded)) this.write(writes, expanded, 'water', 'river_wide');
      }
    }
  }

  private riverShoulders(previous: Point | undefined, current: Point, next: Point | undefined, radius: number): readonly Point[] {
    const horizontal = previous?.y === current.y || next?.y === current.y;
    const vertical = previous?.x === current.x || next?.x === current.x;
    const out: Point[] = [];
    for (let step = 1; step <= radius; step++) {
      if (horizontal && !vertical) {
        out.push({ x: current.x, y: current.y - step }, { x: current.x, y: current.y + step });
      } else if (vertical && !horizontal) {
        out.push({ x: current.x - step, y: current.y }, { x: current.x + step, y: current.y });
      } else {
        out.push({ x: current.x - step, y: current.y }, { x: current.x + step, y: current.y }, { x: current.x, y: current.y - step }, { x: current.x, y: current.y + step });
      }
    }
    return out;
  }

  private variantForRiver(previous: Point | undefined, current: Point, next: Point | undefined): string {
    const a = previous ? directionFrom(current, previous) : undefined;
    const b = next ? directionFrom(current, next) : undefined;
    const directions = [a, b].filter(Boolean).sort().join('_');
    if (directions === 'e_w') return 'river_h';
    if (directions === 'n_s') return 'river_v';
    if (directions) return `river_${directions}`;
    return 'river_end';
  }

  private findBridgeCandidates(waterWrites: readonly TileWrite[]): readonly Point[] {
    const water = new Set(waterWrites.map(key));
    const candidates: Point[] = [];
    for (const write of waterWrites) {
      const horizontalLand = !water.has(`${write.x - 1}:${write.y}`) && !water.has(`${write.x + 1}:${write.y}`);
      const verticalLand = !water.has(`${write.x}:${write.y - 1}`) && !water.has(`${write.x}:${write.y + 1}`);
      if (horizontalLand || verticalLand) candidates.push({ x: write.x, y: write.y });
    }
    return candidates.slice(0, 8);
  }

  private findIsolatedWater(waterWrites: readonly TileWrite[]): readonly Point[] {
    const water = new Set(waterWrites.map(key));
    return waterWrites
      .filter((write) => orthogonalNeighbors(write).every((neighbor) => !water.has(key(neighbor))))
      .map((write) => ({ x: write.x, y: write.y }));
  }

  private isConnected(points: readonly Point[]): boolean {
    if (points.length <= 1) return true;
    const remaining = new Set(points.map(key));
    const queue = [points[0]];
    remaining.delete(key(points[0]));
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;
      for (const neighbor of orthogonalNeighbors(current)) {
        const neighborKey = key(neighbor);
        if (!remaining.has(neighborKey)) continue;
        remaining.delete(neighborKey);
        queue.push(neighbor);
      }
    }
    return remaining.size === 0;
  }

  private write(writes: Map<string, TileWrite>, point: Point, type: TileWrite['type'], variant?: string): void {
    if (!this.isInside(point)) return;
    writes.set(key(point), { x: point.x, y: point.y, type, variant });
  }

  private isInside(point: Point): boolean {
    return point.x >= 0 && point.y >= 0 && point.x < this.config.width && point.y < this.config.height;
  }
}

function orthogonalNeighbors(point: Point): readonly Point[] {
  return [
    { x: point.x + 1, y: point.y },
    { x: point.x - 1, y: point.y },
    { x: point.x, y: point.y + 1 },
    { x: point.x, y: point.y - 1 },
  ];
}

function directionFrom(from: Point, to: Point): string | undefined {
  if (to.x > from.x) return 'e';
  if (to.x < from.x) return 'w';
  if (to.y > from.y) return 's';
  if (to.y < from.y) return 'n';
  return undefined;
}

function samePoint(a: Point, b: Point): boolean {
  return a.x === b.x && a.y === b.y;
}

function key(point: Point): string {
  return `${point.x}:${point.y}`;
}
